import React, { useState, useEffect } from 'react';
import { Modal, View, StyleSheet, Text } from 'react-native';
import CustomLabel from '../../../../components/CustomLabel/CustomLabel';
import CustomInput from '../../../../components/CustomInput';
import CustomButton from '../../../../components/CustomButton';
import { useTheme, Checkbox, } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import CustomDropDown from '../../../../components/CustomDropDown/CustomDropdown';
// import { showMessage } from 'react-native-flash-message';
import { createCommonStyles } from '../../../../styles/commonStyles';

const WorkflowModal = ({
    visible,
    onClose,
    noApprover,
    setNoApprover,
    flexibleApproverMessage,
    setFlexibleApproverMessage,
    editingItem,
    description,
    setDescription,
    selectedCategory,
    categoryOptions,
    setSelectedCategory,
    addNewWorkflow,
    errorMessage,
    setErrorMessage,
    isEditing = false,
    handleEditPress = () => { },
}) => {
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors);
    const { t } = useTranslation();

    const [initialValues, setInitialValues] = useState({
        description: '',
        noApprover: false,
        flexibleApproverMessage: '',
        selectedCategory: '',
    });

    useEffect(() => {
        if (!visible) return;
        if (isEditing && editingItem) {
            const initCategory = editingItem.categoryId ?? editingItem.selectedCategory ?? selectedCategory ?? '';
            setInitialValues({
                description: editingItem.description ?? '',
                noApprover: !!editingItem.noApprover,
                flexibleApproverMessage: editingItem.approverMessage ?? '',
                selectedCategory: initCategory,
            });
            setSelectedCategory && setSelectedCategory(initCategory);
        } else if (!isEditing) {
            setInitialValues({
                description: '',
                noApprover: false,
                flexibleApproverMessage: '',
                selectedCategory: '',
            });
            setSelectedCategory && setSelectedCategory('');
        }
        setErrorMessage && setErrorMessage('');
    }, [visible, isEditing, editingItem]);

    const handleSave = async () => {
        if (!description || description.trim() === '') {
            setErrorMessage(t('common:pleaseEnterDescription'));
            return;
        }
        if (!selectedCategory) {
            setErrorMessage(t('common:pleaseSelectCategory'));
            return;
        }
        if (isEditing) {
            const noChanges =
                description === initialValues.description &&
                noApprover === initialValues.noApprover &&
                flexibleApproverMessage === initialValues.flexibleApproverMessage &&
                selectedCategory === initialValues.selectedCategory;

            if (noChanges) {
                setErrorMessage(t('common:noChangesMade'))
                return;
            }
        }

        setErrorMessage('');
        await addNewWorkflow();
    };

    return (
        <Modal
            animationType="slide"
            transparent={true}
            visible={visible}
            onRequestClose={onClose}
        >
            <View style={styles.modalOverlay}>
                <View style={styles.modalContainer}>
                    <CustomLabel
                        value={
                            isEditing
                                ? editingItem?.description || t('common:editWorkflow')
                                : t('common:addNewWorkflow')
                        }
                        type="TITLE"
                        style={{ textAlign: 'left' }}
                    />
                    {errorMessage ? (
                        <View style={styles.errorMessageContainer}>
                            <Text style={styles.errorMessageText}>{errorMessage}</Text>
                        </View>
                    ) : null}
                    <View style={styles.row}>
                        <View style={styles.subtitle}>
                            <CustomLabel value={t('common:category')} type="SUBTITLE" />
                        </View>
                        <View style={styles.dropdown}>
                            <CustomDropDown
                                multipleSelect={false}
                                defValue={selectedCategory === '' ? null : selectedCategory}
                                value={selectedCategory}
                                setValue={(val) => {
                                    setSelectedCategory(val);
                                    handleEditPress && handleEditPress('selectedCategory', val);
                                }}
                                options={categoryOptions}
                                type="MAININPUT"
                                disabled={false}
                            />
                        </View>
                    </View>
                    <View style={styles.row}>
                        <View style={styles.subtitle}>
                            <CustomLabel value={t('common:description')} type="SUBTITLE" />
                        </View>
                        <View style={styles.input}>
                            <CustomInput
                                value={description ?? ''}
                                setValue={(text) => {
                                    setDescription(text);
                                    handleEditPress('description', text);
                                }}
                                type="MAININPUT"
                                editable={true}
                            />
                        </View>
                    </View>
                    <View style={styles.row}>
                        <View style={styles.subtitle}>
                            <CustomLabel value={t('common:noApprover')} type="SUBTITLE" />
                        </View>
                        <View style={styles.checkbox}>
                            <Checkbox
                                status={noApprover ? 'checked' : 'unchecked'}
                                onPress={() => {
                                    const newValue = !noApprover;
                                    setNoApprover(newValue);
                                    handleEditPress('noApprover', newValue);
                                }}
                                color={noApprover ? '#86BC25' : undefined}
                            />
                        </View>
                    </View>
                    <View style={styles.row}>
                        <View style={styles.subtitle}>
                            <CustomLabel value={t('common:flexibleApproverMessage')} type="SUBTITLE" />
                        </View>
                        <View style={styles.input}>
                            <CustomInput
                                value={flexibleApproverMessage ?? ''}
                                setValue={setFlexibleApproverMessage}
                                type="MAININPUT"
                                editable={true}
                            />
                        </View>
                    </View>
                    <View style={styles.modalButtons}>
                        <CustomButton
                            text={t('common:cancel')}
                            onPress={onClose}
                            type='BUTTON_CANCEL'
                            textTop="0"
                            sz={15}
                        />
                        <View style={{ width: 10 }} />
                        <CustomButton
                            text={t('common:save')}
                            onPress={handleSave}
                            type='BUTTON_SAVE'
                            isGradient={true}
                            textTop="0"
                            sz={15}
                        />
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const makeStyles = () =>
    StyleSheet.create({
        modalOverlay: {
            flex: 1,
            backgroundColor: 'rgba(0,0,0,0.5)',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 20,
        },
        modalContainer: {
            width: '90%',
            backgroundColor: 'white',
            borderRadius: 10,
            padding: 20,
        },
        modalButtons: {
            flexDirection: 'row',
            justifyContent: 'flex-end',
            marginTop: 20,
        },
        checkbox: {
            flex: 1,
            justifyContent: 'flex-start',
            margin: 15
        },
        subtitle: {
            alignItems: 'flex-start',
            width: '30%',
            justifyContent: 'space-between',
            margin: 20,
        },
        input: {
            flex: 1,
            justifyContent: 'flex-start',
        },
        row: {
            flex: 1,
            flexDirection: "row",
            alignItems: 'center',
            width: '95%',
            margin: 5,
        },
        dropdown: {
            flex: 1,
            justifyContent: 'flex-start',
            marginHorizontal: 8,
        },
        errorMessageContainer: {
            backgroundColor: '#f8d7da',
            padding: 10,
            borderRadius: 5,
            marginVertical: 10,
        },
        errorMessageText: {
            color: '#721c24',
            fontWeight: 'bold',
        },
    });

export default WorkflowModal;